import { useQuery } from "@tanstack/react-query";
import { supabase } from "../lib/supabaseClient";

/** Prefix-invalidated by realtime on every `sanctions` change (lib/realtime/globalRealtimeHandlers.ts). */
export const DISCIPLINARY_AUDIT_ISSUES_COUNT_KEY = "disciplinaryAuditIssuesCount";

/**
 * Lightweight, count-only query for the Disciplinary "Audit" badge.
 *
 * Today this counts sanctions that are very likely to require attention:
 * - missing `reason_id`
 * - or linked reason is inactive
 *
 * This is intentionally a cheap query (head/count) so it can run without
 * fetching all sanctions/rules/reasons.
 */
export function useDisciplinaryAuditIssuesCount(options: {
    leagueId?: string;
    enabled?: boolean;
} = {}) {
    return useQuery({
        queryKey: [DISCIPLINARY_AUDIT_ISSUES_COUNT_KEY, options.leagueId],
        queryFn: async () => {
            if (!options.leagueId) return 0;

            // NOTE: We include a (left) join to disciplinary_reasons so we can detect
            // inactive reasons. Missing reason_id is handled explicitly.
            const { count, error } = await supabase
                .from("sanctions")
                .select(
                    "id, disciplinary_reason:disciplinary_reasons!sanctions_reason_id_fkey(is_active)",
                    { count: "exact", head: true }
                )
                .eq("league_id", options.leagueId)
                .eq("is_cancelled", false)
                // PostgREST filtering must reference the real embedded resource name
                // (`disciplinary_reasons`), not the alias (`disciplinary_reason`).
                .or("reason_id.is.null,disciplinary_reasons.is_active.eq.false");

            if (error) {
                console.error("[useDisciplinaryAuditIssuesCount] Error:", error);
                return 0;
            }

            return count ?? 0;
        },
        enabled: options.enabled !== false && Boolean(options.leagueId),
        staleTime: 5 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
    });
}
